import {APP_CONFIG} from '@/config/app.config';
import {useSettingsStore} from '@/store/settings';
import type {VerifyCompleteResponseDto} from '@/features/auth-verify/types';

const ANTIFORGERY_TOKEN_HEADER = '__RequestVerificationToken';

function readAntiforgeryToken(): string {
    const input = document.querySelector<HTMLInputElement>('input[name="__RequestVerificationToken"]');
    return input?.value ?? '';
}

function buildUrl(path: string): string {
    const baseUrl = APP_CONFIG.API_ENDPOINT;
    const baseHref = baseUrl instanceof URL ? baseUrl.href : baseUrl;
    return new URL(`/api/app/${path}`, baseHref).href;
}

export interface VerifyCallResult<T> {
    ok: boolean;
    data?: T;
    errorCode?: string;
    networkError?: boolean;
}

export interface VerifyInitiateResponse {
    verificationCode?: string;
}

export interface VerifyWebEidInitiateResponse {
    base64EncodedNonce: string;
    expirationTime?: string;
}

async function postJson<T>(path: string, body: Record<string, unknown>): Promise<VerifyCallResult<T>> {
    const settings = useSettingsStore();
    let response: Response;
    try {
        response = await fetch(buildUrl(path), {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                'Accept-Language': settings.cultureInfo,
                [ANTIFORGERY_TOKEN_HEADER]: readAntiforgeryToken()
            },
            body: JSON.stringify(body)
        });
    } catch {
        return {ok: false, networkError: true};
    }

    if (response.status === 401) {
        window.location.href = '/Authent/Logout';
        throw new Error('Unauthorized');
    }

    if (response.ok) {
        try {
            const data = (await response.json()) as T;
            return {ok: true, data};
        } catch {
            return {ok: false, networkError: true};
        }
    }

    try {
        const errorBody = (await response.json()) as {errorCode?: string};
        return {ok: false, errorCode: errorBody?.errorCode};
    } catch {
        return {ok: false, networkError: true};
    }
}

function buildCompleteRequest(path: string, body: Record<string, unknown>): {url: string; init: RequestInit} {
    const settings = useSettingsStore();
    return {
        url: buildUrl(path),
        init: {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                'Accept-Language': settings.cultureInfo,
                [ANTIFORGERY_TOKEN_HEADER]: readAntiforgeryToken()
            },
            body: JSON.stringify(body)
        }
    };
}

export const verifyApi = {
    initiateMobileId(body: {phoneNumber: string; identityNumber: string}): Promise<VerifyCallResult<VerifyInitiateResponse>> {
        return postJson<VerifyInitiateResponse>('auth/verify/mobile-id/initiate', body);
    },

    completeMobileIdRequest(body: {purpose: string; resourceId: string}) {
        return buildCompleteRequest('auth/verify/mobile-id/complete', body);
    },

    initiateSmartId(body: {identityNumber: string}): Promise<VerifyCallResult<VerifyInitiateResponse>> {
        return postJson<VerifyInitiateResponse>('auth/verify/smart-id/initiate', body);
    },

    completeSmartIdRequest(body: {purpose: string; resourceId: string}) {
        return buildCompleteRequest('auth/verify/smart-id/complete', body);
    },

    initiateWebEid(): Promise<VerifyCallResult<VerifyWebEidInitiateResponse>> {
        return postJson<VerifyWebEidInitiateResponse>('auth/verify/web-eid/initiate', {});
    },

    completeWebEid(body: {
        algorithm: string;
        format: string;
        signature: string;
        unverifiedCertificate: string;
        purpose: string;
        resourceId: string;
    }): Promise<VerifyCallResult<VerifyCompleteResponseDto>> {
        return postJson<VerifyCompleteResponseDto>('auth/verify/web-eid/complete', body);
    }
};
